import React, { useEffect, useState } from 'react';
import { Button, Form, FormInstance, Input, InputNumber, Table } from 'antd';
import { IOrderItem } from '@/interfaces/order';
import { round } from 'lodash';
import StockSelectorModal from '@/components/stock/stock-selector-modal';
import { PlusIcon } from '@heroicons/react/24/solid';
import { v4 as uuidv4 } from 'uuid';
import { IStock } from '@/interfaces/stock';
import { formatNumber } from '@/utils/dataUtils';
import { ICndnFreeForm } from '@/interfaces/cndnFreeForm';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
interface IProps {
  form: FormInstance<ICndnFreeForm>;
  _customerId: number | undefined;
  handleOrderItem: (
    orderItem: Partial<IOrderItem>[],
    orderSummary: number,
    orderVatRate: number,
    orderDiscount: number,
  ) => void;
}
const OrderCndnFreeFomTable = ({
  form,
  handleOrderItem,
  _customerId,
}: IProps) => {
  const [discount, setDiscount] = useState(0);
  const [vatRate, setVatRate] = useState(7);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [data, setData] = useState<Partial<IOrderItem>[]>([]);
  const [isAddStock, setIsAddStock] = useState(false);
  const isEditing = (record: Partial<IOrderItem>) => record.key === editingKey;

  const edit = (record: Partial<IOrderItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<IOrderItem>;
      const newData = [...data];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          unitPrice: round(Number(item.unitPrice || 0), 2),
          quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });
        setData(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };
  interface EditableCellProps extends React.HTMLAttributes<unknown> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text';
    record: Partial<IOrderItem>;
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };
  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      editable: editingIndex === 'lotNumber',
      className: 'font-bold',
      align: 'center',
      render: (_: string, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'lotNumber')}
        >
          {_ ?? ''}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      editable: editingIndex === 'quantity',
      dataIndex: 'quantity',
      className: 'font-bold text-green-700',
      align: 'right',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'quantity')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'ราคา',
      editable: editingIndex === 'unitPrice',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-blue-700',
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'unitPrice')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'รวมเงิน',
      width: 150,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: IOrderItem) =>
        formatNumber(round(rec.quantity * (rec.unitPrice || 0), 2), 2),
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IOrderItem) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => onDeleteItem(rec)}
        />
      ),
    },
  ];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mergeColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IOrderItem) => {
        return {
          record,
          inputType:
            typeof record[col.dataIndex as keyof IOrderItem] === 'number' &&
            col.dataIndex === editingIndex
              ? 'number'
              : 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          isOrder: true,
        };
      },
    };
  });

  const onRowClick = (record: Partial<IOrderItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const onDeleteItem = (item: IOrderItem) => {
    setData(prev => prev.filter(p => p.key !== item.key));
  };

  const onAddOrderItem = (items: IStock[]) => {
    const orderItems: Partial<IOrderItem>[] = items.map(i => {
      const orderItem: Partial<IOrderItem> = {
        ...i,
        key: uuidv4(),
        id: 0,
        stockId: i.id,
        quantity: i.quantity,
        remain: i.quantity,
        lotNumber: i.code,
        qtId: i.qtId,
        qtNo: i.qtNo,
      };
      return orderItem;
    });
    console.log(orderItems);
    setData([...data, ...orderItems]);
  };

  let totalPrice = 0;
  let summaryPrice = 0;
  data?.forEach(({ quantity, unitPrice }) => {
    totalPrice += Number(round(Number(quantity) * Number(unitPrice), 2));
  });
  const vatPrice = round(
    ((Math.abs(totalPrice) - discount) * vatRate) / 100,
    2,
  );
  summaryPrice = Math.abs(totalPrice) - discount + vatPrice;

  useEffect(() => {
    handleOrderItem(data, summaryPrice, vatRate, discount);
  }, [data, discount, vatRate, totalPrice, vatPrice, summaryPrice]);

  return (
    <>
      <div className="flex w-full mt-2">
        <Form form={form} component={false}>
          <Table
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            locale={{
              emptyText: 'เพิ่มรายการใหม่',
            }}
            size="middle"
            bordered
            dataSource={data}
            columns={mergeColumns}
            className="w-full"
            rowClassName="editable-row"
            pagination={false}
            scroll={{ x: 1200 }}
            summary={() => {
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={8}
                      className="font-bold items-end"
                      align="right"
                    >
                      รวมเงิน
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(totalPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end "
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={8}
                      className="font-bold items-end"
                      align="right"
                    >
                      ส่วนลด (บาท)
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={5}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      <InputNumber
                        className="w-full"
                        value={discount}
                        style={{ textAlignLast: 'right' }}
                        controls={false}
                        decimalSeparator="."
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value =>
                          value?.replace(/\$\s?|(,*)/g, '') as unknown as number
                        }
                        precision={2}
                        onChange={value => {
                          setDiscount(value || 0);
                        }}
                        min={0}
                      />
                    </Table.Summary.Cell>{' '}
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={8}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดรวมหลังหักส่วนลด
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(totalPrice - discount, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={9}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={8}
                      className="font-bold items-end"
                      align="right"
                    >
                      <InputNumber
                        value={vatRate}
                        onChange={value => {
                          setVatRate(value || 0);
                        }}
                        min={0}
                        suffix="%"
                      />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(vatRate, 2)}
                    </Table.Summary.Cell>{' '}
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={8}
                      className="font-bold items-end"
                      align="right"
                    >
                      ยอดสุทธิ
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={7}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    >
                      {formatNumber(summaryPrice, 2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={8}
                      colSpan={1}
                      className="font-bold items-end"
                      align="right"
                    />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Form>
      </div>
      <div className="flex justify-end mt-3">
        <Button
          disabled={!_customerId}
          type="default"
          className="text-blue-700 font-bold"
          onClick={() => setIsAddStock(true)}
        >
          <PlusIcon className="w-3 h-3" /> เพิ่มรายการ
        </Button>
        <StockSelectorModal
          handleCancel={() => setIsAddStock(false)}
          handleOk={onAddOrderItem}
          open={isAddStock}
          exists={[]}
          customerId={Number(_customerId)}
        />
      </div>
    </>
  );
};
export default OrderCndnFreeFomTable;
